import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useParams,
} from "react-router-dom";
import { useContext } from "react";
import Panel from "@main/panel-components/Panel";
import Login from "@beeldit/user-and-access/auth/pages/Login";
import { AuthProvider } from "@beeldit/user-and-access/auth/AuthContext";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";

import { TenantAndCommerceProvider } from "./contexts/TenantAndCommerceContext";

import { ConfigurationsProvider } from "./contexts/ConfigurationsContext";

import { User } from "@beeldit/user-and-access/users/models/User";

import { EditActionProvider as UserEditActionProvider } from "@beeldit/user-and-access/users/actions/edit/EditActionContext";
import { ListActionProvider as UserListActionProvider } from "@beeldit/user-and-access/users/actions/list/ListActionContext";
import { CreateActionProvider as UserCreateActionProvider } from "@beeldit/user-and-access/users/actions/create/CreateActionContext";
import { RemoveActionProvider as UserRemoveActionProvider } from "@beeldit/user-and-access/users/actions/remove/RemoveActionContext";
import UserPage from "@beeldit/user-and-access/users/pages/UserPage";
import { ChangePasswordActionProvider as UserChangePasswordProvider } from "@beeldit/user-and-access/users/actions/change-password/ChangePasswordActionContext";
import { ResetPasswordActionProvider as UserResetPasswordProvider } from "@beeldit/user-and-access/users/actions/reset-password-mail/ResetPasswordActionContext";
import { ActiveOrDisableActionProvider as UserActiveOrDisableProvider } from "@beeldit/user-and-access/users/actions/active-or-disable-account/ActiveOrDisableActionContext";

import HeaderDashboardPage from "@bloonde/cms/cms-headers/pages/HeaderDashboardPage";
import { DetailsActionProvider as HeaderDetailsActionProvider } from "@bloonde/cms/cms-headers/actions/details/DetailsActionContext";

import FooterDashboardPage from "@bloonde/cms/cms-footers/pages/FooterDashboardPage";
import { DetailsActionProvider as FooterDetailsActionProvider } from "@bloonde/cms/cms-footers/actions/details/DetailsActionContext";

import PageRouterWrapper from "@bloonde/cms/cms-pages/wrappers/PageRouterWrapper";
import { DetailsActionProvider as PageDetailsActionProvider } from "@bloonde/cms/cms-pages/actions/details/DetailsActionContext";
import PageEditionPage from "@bloonde/cms/cms-pages/pages/PageEditionPage";
import CustomersRouterWrapper from "@main/customers/wrappers/CustomersRouterWrapper";
import { DetailsActionProvider as CustomerDetailsActionProvider } from "@main/customers/actions/details/DetailsActionContext";
import CustomerDashboardPage from "@main/customers/pages/CustomerDashboardPage";

import ProducersRouterWrapper from "@main/producers/wrappers/ProducersRouterWrapper";
import { DetailsActionProvider as ProducerDetailsActionProvider } from "@main/producers/actions/details/DetailsActionContext";
import ProducerDashboardPage from "@main/producers/pages/ProducerDashboardPage";
import ProducerProfilePage from "@main/producers/pages/ProducerProfilePage";

import RequestResetPassword from "@beeldit/user-and-access/auth/pages/RequestResetPassword";
import ResetPassword from "@beeldit/user-and-access/auth/pages/ResetPassword";
import AccountConfirmation from "@beeldit/user-and-access/auth/pages/AccountConfirmation";
import CategoriesRouterWrapper from "@beeldit/ecommerce/categories/wrappers/CategoriesRouterWrapper";
import ProductsRouterWrapper from "@beeldit/ecommerce/products/wrappers/ProductsRouterWrapper";
import { CreateActionProvider as ProductCreateActionProvider } from "@beeldit/ecommerce/products/actions/create/CreateActionContext";
import { DetailsActionProvider as ProductDetalsActionProvider } from "@beeldit/ecommerce/products/actions/details/DetailsActionContext";
import ProductDashboardPage from "@beeldit/ecommerce/products/pages/ProductDashboardPage";
import ProducerSelector from "@main/producers/components/ProducerSelector";

import { ListActionProvider as SuperOrderListProvider } from "@beeldit/ecommerce/super-orders/actions/list/ListActionContext";
import { CancelActionProvider as SuperOrderCancelProvider } from "@beeldit/ecommerce/super-orders/actions/cancel/CancelActionContext";
import { DetailsActionProvider as SuperOrderDetailsProvider } from "@beeldit/ecommerce/super-orders/actions/details/DetailsActionContext";
import { RequestInvoiceActionProvider as SuperOrderRequestInvoiceProvider } from "@beeldit/ecommerce/super-orders/actions/request-invoice/RequestInvoiceActionContext";

import SuperOrdersPage from "@beeldit/ecommerce/super-orders/pages/SuperOrdersPage";
import SuperOrderDetailPage from "@beeldit/ecommerce/super-orders/pages/SuperOrderDetailPage";

import { ListActionProvider as OrderListProvider } from "@beeldit/ecommerce/orders/actions/list/ListActionContext";
import OrdersPage from "@beeldit/ecommerce/orders/pages/OrdersPage";
import { ChangeStatusActionProvider } from "@beeldit/ecommerce/orders/actions/change-status/ChangeStatusActionContext";
import { UploadInvoiceActionProvider } from "@beeldit/ecommerce/orders/actions/upload-invoice/UploadInvoiceActionContext";

import { DetailsActionProvider as OrderDetailsProvider } from "@beeldit/ecommerce/orders/actions/details/DetailsActionContext";
import OrderDetailPage from "@beeldit/ecommerce/orders/pages/OrderDetailPage";
import CouponsRouterWrapper from "@beeldit/coupons/coupons/wrappers/CouponsRouterWrapper";

import { DetailsActionProvider as ConfigurationDetailsActionProvider } from "@main/configurations/actions/details/DetailsActionContext";
import ConfigurationDetailsPage from "@main/configurations/pages/ConfigurationDetailsPage";
import { EditObservationsActionProvider } from "@beeldit/ecommerce/super-orders/actions/edit-observations/EditObservationsActionContext";
import DashboardPage from "@main/dashboard/pages/DashboardPage";
import TagsRouterWrapper from "@bloonde/cms/cms-tags/wrappers/TagsRouterWrapper";
import OptionsRouterWrapper from "@beeldit/ecommerce/options/wrappers/OptionsRouterWrapper";
import { EditBillingAddressActionProvider } from "@beeldit/ecommerce/super-orders/actions/edit-billing-address/EditBillingAddressActionContext";
import { EditShippingAddressActionProvider } from "@beeldit/ecommerce/super-orders/actions/edit-shipping-address/EditShippingAddressActionContext";
import { ConfigurationActionProvider } from "./producers/actions/configuration/ConfigurationActionContext";
import BalanceRouterWrapper from "@bloonde/billing/balances/wrappers/BalanceRouterWrapper";
import { ListActionProvider as BalanceListProvider } from "@bloonde/billing/balances/actions/list/ListActionContext";
import ProducerBalancesPage from "@bloonde/billing/balances/pages/ProducerBalancesPage";

const redirectFunction = (user: User) => {
  if (user.role === "producer") {
    return "/panel/1/1/producer/orders";
  }
  if (user.role === "admin") {
    return "/panel/1/1/dashboard";
  }
  return "/panel";
};

// Redirección dinámica para la ruta índice /panel/:tenantId/:commerceId
function PanelIndexRedirect() {
  const { user, tokenLoaded } = useContext(AuthContext);
  const { tenantId, commerceId } = useParams();

  if (!tokenLoaded) return null;

  const tId = tenantId ?? "1";
  const cId = commerceId ?? "1";

  if (user?.role === "producer") {
    return <Navigate to={`/panel/${tId}/${cId}/producer/orders`} replace />;
  }
  // Por defecto (admin u otros) a dashboard
  return <Navigate to={`/panel/${tId}/${cId}/dashboard`} replace />;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AuthProvider>
        <ConfigurationsProvider>
          <div className="App">
            <Outlet />
          </div>
        </ConfigurationsProvider>
      </AuthProvider>
    ),
    children: [
      {
        path: "/login",
        element: (
          <Login
            redirectFunction={(user: User) => {
              return redirectFunction(user);
            }}
          />
        ),
      },
      {
        path: "/request-reset-password",
        element: <RequestResetPassword />,
      },
      {
        path: "/resetPassword",
        element: <ResetPassword />,
      },
      {
        path: "/account-confirmation",
        element: <AccountConfirmation />,
      },
      {
        path: "/",
        index: true,
        element: <Navigate to="/panel/1/1" />,
      },
      {
        path: "/panel",
        index: true,
        element: <Navigate to="/panel/1/1" />,
      },
      {
        path: "/panel/:tenantId/:commerceId",
        index: true,
        element: <PanelIndexRedirect />,
      },
      {
        path: "/panel/:tenantId/:commerceId",
        element: (
          <TenantAndCommerceProvider>
            <Panel />
          </TenantAndCommerceProvider>
        ),
        children: [
          {
            path: "dashboard",
            element: <DashboardPage />,
          },
          {
            path: "users",
            element: (
              <UserListActionProvider>
                <UserRemoveActionProvider>
                  <UserCreateActionProvider>
                    <UserEditActionProvider>
                      <UserActiveOrDisableProvider>
                        <UserResetPasswordProvider>
                          <UserChangePasswordProvider>
                            <UserPage
                              filters={{ role: "admin" }}
                              formDefaultValues={{ role: "admin" }}
                            />
                          </UserChangePasswordProvider>
                        </UserResetPasswordProvider>
                      </UserActiveOrDisableProvider>
                    </UserEditActionProvider>
                  </UserCreateActionProvider>
                </UserRemoveActionProvider>
              </UserListActionProvider>
            ),
          },
          {
            path: "retails",
            element: (
              <CustomersRouterWrapper
                key={"retails"}
                title={"retails"}
                filters={{ role: "customer" }}
                formDefaultValues={{
                  user: { role: "customer" },
                  create_address: true,
                }}
              />
            ),
          },
          {
            path: "retails/:customerId",
            element: (
              <CustomerDetailsActionProvider>
                <CustomerDashboardPage
                  key={"retails-details"}
                  title={"retails"}
                  formDefaultValues={{ user: { role: "customer" } }}
                />
              </CustomerDetailsActionProvider>
            ),
          },
          {
            path: "dealers",
            element: (
              <CustomersRouterWrapper
                key={"dealers"}
                title={"dealers"}
                filters={{ role: "dealer" }}
                formDefaultValues={{
                  user: { role: "dealer" },
                  create_address: true,
                }}
              />
            ),
          },
          {
            path: "dealers/:customerId",
            element: (
              <CustomerDetailsActionProvider>
                <CustomerDashboardPage
                  key={"dealers-detail"}
                  title={"dealers"}
                  formDefaultValues={{ user: { role: "dealer" } }}
                />
              </CustomerDetailsActionProvider>
            ),
          },
          {
            path: "producers",
            element: (
              <ProducersRouterWrapper
                formDefaultValues={{ user: { role: "producer" } }}
              />
            ),
          },
          {
            path: "balances",
            element: <BalanceRouterWrapper></BalanceRouterWrapper>,
          },
          {
            path: "producers/:producerId",
            element: (
              <ProducerDetailsActionProvider>
                <ConfigurationActionProvider>
                  <ProducerDashboardPage
                    formDefaultValues={{ user: { role: "producer" } }}
                  />
                </ConfigurationActionProvider>
              </ProducerDetailsActionProvider>
            ),
          },
          {
            path: "products",
            element: <ProductsRouterWrapper />,
          },
          {
            path: "products/new",
            element: (
              <ProductCreateActionProvider>
                <ProductDashboardPage
                  producer_type={"App\\Models\\Producer"}
                  roles={["producer"]}
                  userProducerIdProp={"producer.id"}
                  producerSelectorComponent={ProducerSelector}
                />
              </ProductCreateActionProvider>
            ),
          },
          {
            path: "products/:productId",
            element: (
              <ProductDetalsActionProvider>
                <ProductDashboardPage
                  producer_type={"App\\Models\\Producer"}
                  roles={["producer"]}
                  userProducerIdProp={"producer.id"}
                  producerSelectorComponent={ProducerSelector}
                />
              </ProductDetalsActionProvider>
            ),
          },
          {
            path: "coupons",
            element: <CouponsRouterWrapper />,
          },
          {
            path: "admin/orders",
            element: (
              <SuperOrderListProvider>
                <SuperOrderCancelProvider>
                  <SuperOrderRequestInvoiceProvider>
                    <SuperOrdersPage />
                  </SuperOrderRequestInvoiceProvider>
                </SuperOrderCancelProvider>
              </SuperOrderListProvider>
            ),
          },
          {
            path: "admin/order/:orderId",
            element: (
              <SuperOrderDetailsProvider>
                <ChangeStatusActionProvider>
                  <UploadInvoiceActionProvider>
                    <EditObservationsActionProvider>
                      <EditBillingAddressActionProvider>
                        <EditShippingAddressActionProvider>
                          <SuperOrderDetailPage />
                        </EditShippingAddressActionProvider>
                      </EditBillingAddressActionProvider>
                    </EditObservationsActionProvider>
                  </UploadInvoiceActionProvider>
                </ChangeStatusActionProvider>
              </SuperOrderDetailsProvider>
            ),
          },
          {
            path: "producer/orders",
            element: (
              <OrderListProvider>
                <ChangeStatusActionProvider>
                  <OrdersPage />
                </ChangeStatusActionProvider>
              </OrderListProvider>
            ),
          },
          {
            path: "producer/profile",
            element: (
              <ProducerDetailsActionProvider>
                <ConfigurationActionProvider>
                  <ProducerProfilePage
                    formDefaultValues={{ user: { role: "producer" } }}
                  />
                </ConfigurationActionProvider>
              </ProducerDetailsActionProvider>
            ),
          },
          {
            path: "producer/invoice-orders",
            element: (
              <OrderListProvider>
                <ChangeStatusActionProvider>
                  <OrdersPage
                    filters={{ request_invoice: 1 }}
                    key="invoice-orders"
                  />
                </ChangeStatusActionProvider>
              </OrderListProvider>
            ),
          },
          {
            path: "producer/balances",
            element: (
              <BalanceListProvider>
                <ProducerBalancesPage />
              </BalanceListProvider>
            ),
          },
          {
            path: "producer/order/:orderId",
            element: (
              <OrderDetailsProvider>
                <ChangeStatusActionProvider>
                  <UploadInvoiceActionProvider>
                    <OrderDetailPage />
                  </UploadInvoiceActionProvider>
                </ChangeStatusActionProvider>
              </OrderDetailsProvider>
            ),
          },
          {
            path: "pages",
            element: (
              <PageRouterWrapper
                config={{ showCreation: false, title: "pages" }}
                filters={{ type: 1 }}
                initialUrlFragment="pages"
                key="pages"
              />
            ),
          },
          {
            path: "pages/:pageId",
            element: (
              <PageDetailsActionProvider>
                <PageEditionPage title={"page"} />
              </PageDetailsActionProvider>
            ),
          },
          {
            path: "blog-categories",
            element: <TagsRouterWrapper />,
          },
          {
            path: "blog",
            element: (
              <PageRouterWrapper
                config={{ showCreation: true, title: "blog" }}
                filters={{ type: 3 }}
                formDefaultValues={{
                  schema_slug: "blog-article",
                  type: "blog",
                  sections: [
                    {
                      type: 28,
                      status: 1,
                      content: [],
                    },
                    {
                      type: 2,
                      status: 1,
                      content: { img: [] },
                    },
                    {
                      type: 3,
                      status: 1,
                      content: { wysiwyg: { es: null, en: null, pt: null } },
                    },
                  ],
                }}
                initialUrlFragment="blog"
                key="blog"
              />
            ),
          },
          {
            path: "blog/:pageId",
            element: (
              <PageDetailsActionProvider>
                <PageEditionPage title={"article"} />
              </PageDetailsActionProvider>
            ),
          },
          {
            path: "header",
            element: (
              <HeaderDetailsActionProvider>
                <HeaderDashboardPage />
              </HeaderDetailsActionProvider>
            ),
          },
          {
            path: "footer",
            element: (
              <FooterDetailsActionProvider>
                <FooterDashboardPage />
              </FooterDetailsActionProvider>
            ),
          },
          {
            path: "categories",
            element: <CategoriesRouterWrapper />,
          },
          {
            path: "options",
            element: <OptionsRouterWrapper />,
          },
          {
            path: "configurations",
            element: (
              <ConfigurationDetailsActionProvider>
                <ConfigurationDetailsPage />
              </ConfigurationDetailsActionProvider>
            ),
          },
        ],
      },
      {
        path: "/panel",
        element: (
          <TenantAndCommerceProvider>
            <Panel />
          </TenantAndCommerceProvider>
        ),
        children: [],
      },
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
